/**
 * Media (images and videos) property update utilities for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

// Track when drag ended for each image to prevent position updates immediately after
const dragEndTimestamps = new Map(); // Map<imageId, timestamp>
const DRAG_END_COOLDOWN_MS = 500; // Don't update position for 500ms after drag end

/**
 * Updates image properties with aspect ratio handling
 * @param {string} id - Image ID
 * @param {Object} newProps - New properties to apply
 * @param {boolean} isDragging - Whether this is a drag operation
 * @param {Array} images - Current images array
 * @param {Array} offCanvasImages - Current off-canvas images array
 * @param {Function} setImages - Function to set images
 * @param {Function} setOffCanvasImages - Function to set off-canvas images
 * @param {Function} setSelectedObject - Function to set selected object
 * @param {Function} setSelectedImage - Function to set selected image
 * @param {Function} setHasUnsavedChanges - Function to set unsaved changes flag
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Object} Updated image object
 */
export const updateImageProperties = (
  id,
  newProps,
  isDragging,
  images,
  offCanvasImages,
  setImages,
  setOffCanvasImages,
  setSelectedObject,
  setSelectedImage,
  setHasUnsavedChanges,
  saveToHistory,
  isUndoingRedoing,
  skipHistorySave = false, // New parameter to skip history saves (e.g., when called from onTransformEnd)
  isElementBeingEditedRef = null, // Ref to check if element is being edited
  setPendingChanges = null, // Function to set pending changes flag
  latestUpdatedImagesRef = null, // Ref to store latest updated images array from property changes
  pendingChangesRef = null, // Ref to track pending changes state
  isManualInput = false, // New parameter to indicate manual input changes (bypasses cooldown)
) => {
  let updatedImg = null;
  let updatedOffCanvasImages = null; // Declare at function level to use in skipHistorySave logic

  // Safety check: ensure images is an array
  if (!Array.isArray(images)) {
    return null;
  }

  // Update main canvas images
  const updatedImages = images.map((img) => {
    if (img.id === id) {
      let processedProps = { ...newProps };

      // DEBUG: Log coordinate changes when toggling isOnTopLayer
      if (processedProps.isOnTopLayer !== undefined) {
      }

      // Handle aspect ratio maintenance if both width and height are being updated
      // Skip aspect ratio correction during image replacement or crop operations
      if (
        img.maintainAspectRatio &&
        processedProps.width !== undefined &&
        processedProps.height !== undefined &&
        !processedProps.isReplacement && // Skip aspect ratio correction during replacement
        !processedProps.isCropOperation // Skip aspect ratio correction during crop operations
      ) {
        const aspectRatio = img.originalAspectRatio || img.width / img.height;

        const newWidth = Number(processedProps.width);
        const newHeight = Number(processedProps.height);

        // Check if the provided dimensions match the aspect ratio
        const providedRatio = newWidth / newHeight;
        const ratioTolerance = 0.01; // Allow small tolerance for floating point errors

        if (Math.abs(providedRatio - aspectRatio) > ratioTolerance) {
          // Dimensions don't match aspect ratio, adjust height based on width
          const correctedHeight =
            Math.round((newWidth / aspectRatio) * 100) / 100;
          processedProps.height = correctedHeight;
        }
      }

      // Remove temporary flags before merging
      const { isReplacement, isCropOperation, ...cleanProps } = processedProps;

      const merged = {
        ...img,
        width:
          processedProps.width !== undefined
            ? Number(processedProps.width)
            : img.width,
        height:
          processedProps.height !== undefined
            ? Number(processedProps.height)
            : img.height,
        x: processedProps.x !== undefined ? Number(processedProps.x) : img.x,
        y: processedProps.y !== undefined ? Number(processedProps.y) : img.y,
        rotation:
          processedProps.rotation !== undefined
            ? Number(processedProps.rotation)
            : img.rotation,
        scaleX:
          processedProps.scaleX !== undefined
            ? Number(processedProps.scaleX)
            : img.scaleX,
        scaleY:
          processedProps.scaleY !== undefined
            ? Number(processedProps.scaleY)
            : img.scaleY,
        ...cleanProps,
      };
      // CRITICAL FIX: Include position from newProps even during drag for selectedObject update
      // This ensures ImageEditor's useEffect runs and updates the x/y input values during drag
      updatedImg = {
        ...merged,
        // Include position from newProps even during drag for selectedObject update
        x: processedProps.x !== undefined ? Number(processedProps.x) : merged.x,
        y: processedProps.y !== undefined ? Number(processedProps.y) : merged.y,
        type: "Image",
      };

      // DEBUG: Log final merged result when toggling isOnTopLayer
      if (processedProps.isOnTopLayer !== undefined) {
      }

      return merged;
    }
    return img;
  });

  if (setImages) {
    // CRITICAL: Use functional update to avoid stale closure issues
    // This ensures we always work with the latest state, preventing position from reverting
    setImages((prevImages) => {
      // Recalculate the update based on the latest state to avoid stale data issues
      const latestImage = prevImages.find((img) => img.id === id);
      if (!latestImage) {
        // Image not found in latest state, return unchanged
        return prevImages;
      }

      // Apply newProps to the latest image state
      let processedProps = { ...newProps };

      // Handle aspect ratio maintenance if both width and height are being updated
      if (
        latestImage.maintainAspectRatio &&
        processedProps.width !== undefined &&
        processedProps.height !== undefined &&
        !processedProps.skipAspectRatioCorrection &&
        !processedProps.isReplacement &&
        !processedProps.isCropOperation
      ) {
        const aspectRatio =
          latestImage.originalAspectRatio ||
          latestImage.width / latestImage.height;
        const newWidth = Number(processedProps.width);
        const newHeight = Number(processedProps.height);
        const providedRatio = newWidth / newHeight;
        const ratioTolerance = 0.01;

        if (Math.abs(providedRatio - aspectRatio) > ratioTolerance) {
          const correctedHeight =
            Math.round((newWidth / aspectRatio) * 100) / 100;
          processedProps.height = correctedHeight;
        }
      }

      // Remove temporary flags
      const {
        isReplacement,
        isCropOperation,
        skipAspectRatioCorrection,
        ...cleanProps
      } = processedProps;

      // CRITICAL FIX: During drag operations, don't update position in updateImageProperties
      // Position will be finalized in handleImageDragEnd, and updating it here can cause race conditions
      // that lead to position reverting. Also don't update position immediately after drag end (cooldown period).
      // EXCEPTION: Manual input changes (isManualInput=true) should ALWAYS update position immediately
      const dragEndTime = dragEndTimestamps.get(id);
      const isInCooldown =
        dragEndTime && Date.now() - dragEndTime < DRAG_END_COOLDOWN_MS;
      // Use the same logic for all images: only update position when not dragging and (not in cooldown OR is manual input)
      const shouldUpdatePosition =
        !isDragging && (!isInCooldown || isManualInput);

      if (isInCooldown && !isManualInput) {
      }

      // Merge with latest state
      const merged = {
        ...latestImage,
        width:
          processedProps.width !== undefined
            ? Number(processedProps.width)
            : latestImage.width,
        height:
          processedProps.height !== undefined
            ? Number(processedProps.height)
            : latestImage.height,
        // Only update position if not dragging (position is finalized in handleImageDragEnd)
        x:
          shouldUpdatePosition && processedProps.x !== undefined
            ? Number(processedProps.x)
            : latestImage.x,
        y:
          shouldUpdatePosition && processedProps.y !== undefined
            ? Number(processedProps.y)
            : latestImage.y,
        rotation:
          processedProps.rotation !== undefined
            ? Number(processedProps.rotation)
            : latestImage.rotation,
        scaleX:
          processedProps.scaleX !== undefined
            ? Number(processedProps.scaleX)
            : latestImage.scaleX,
        scaleY:
          processedProps.scaleY !== undefined
            ? Number(processedProps.scaleY)
            : latestImage.scaleY,
        ...cleanProps,
      };

      // CRITICAL FIX: Set updatedImg for main canvas images to ensure setSelectedObject is called
      // Include position from newProps even when isDragging=true (for selectedObject update only)
      // This ensures ImageEditor's useEffect runs and updates the x/y input values during drag
      // IMPORTANT: Always create updatedImg with current position, even during drag
      // Set it BEFORE returning so it's available synchronously
      if (!updatedImg) {
        updatedImg = {
          ...merged,
          // Include position from newProps even during drag for selectedObject update
          x:
            processedProps.x !== undefined
              ? Number(processedProps.x)
              : merged.x,
          y:
            processedProps.y !== undefined
              ? Number(processedProps.y)
              : merged.y,
          type: "Image",
        };
      }

      return prevImages.map((img) => (img.id === id ? merged : img));
    });
  } else {
  }

  // Update off-canvas images if present
  // Safety check: ensure offCanvasImages is an array
  if (!Array.isArray(offCanvasImages)) {
    // If offCanvasImages is not provided or not an array, skip updating it
  } else {
    updatedOffCanvasImages = offCanvasImages.map((img) => {
      if (img.id === id) {
        let processedProps = { ...newProps };

        // Handle aspect ratio maintenance if both width and height are being updated
        // Skip aspect ratio correction during image replacement or crop operations
        if (
          img.maintainAspectRatio &&
          processedProps.width !== undefined &&
          processedProps.height !== undefined &&
          !processedProps.skipAspectRatioCorrection &&
          !processedProps.isReplacement && // Skip aspect ratio correction during replacement
          !processedProps.isCropOperation // Skip aspect ratio correction during crop operations
        ) {
          const aspectRatio = img.originalAspectRatio || img.width / img.height;
          const newWidth = Number(processedProps.width);
          const newHeight = Number(processedProps.height);

          // Check if the provided dimensions match the aspect ratio
          const providedRatio = newWidth / newHeight;
          const ratioTolerance = 0.01; // Allow small tolerance for floating point errors

          if (Math.abs(providedRatio - aspectRatio) > ratioTolerance) {
            // Dimensions don't match aspect ratio, adjust height based on width
            const correctedHeight =
              Math.round((newWidth / aspectRatio) * 100) / 100;
            processedProps.height = correctedHeight;
          }
        }

        // Remove temporary flags before merging
        const {
          isReplacement,
          isCropOperation,
          skipAspectRatioCorrection,
          ...cleanProps
        } =
          processedProps;

        const merged = {
          ...img,
          width:
            processedProps.width !== undefined
              ? Number(processedProps.width)
              : img.width,
          height:
            processedProps.height !== undefined
              ? Number(processedProps.height)
              : img.height,
          x: processedProps.x !== undefined ? Number(processedProps.x) : img.x,
          y: processedProps.y !== undefined ? Number(processedProps.y) : img.y,
          rotation:
            processedProps.rotation !== undefined
              ? Number(processedProps.rotation)
              : img.rotation,
          scaleX:
            processedProps.scaleX !== undefined
              ? Number(processedProps.scaleX)
              : img.scaleX,
          scaleY:
            processedProps.scaleY !== undefined
              ? Number(processedProps.scaleY)
              : img.scaleY,
          ...cleanProps,
        };
        // CRITICAL FIX: Include position from newProps even during drag for selectedObject update
        // This ensures ImageEditor's useEffect runs and updates the x/y input values during drag
        updatedImg = {
          ...merged,
          // Include position from newProps even during drag for selectedObject update
          x:
            processedProps.x !== undefined
              ? Number(processedProps.x)
              : merged.x,
          y:
            processedProps.y !== undefined
              ? Number(processedProps.y)
              : merged.y,
          type: "Image",
        };
        return merged;
      }
      return img;
    });
    // CRITICAL: Set ref synchronously when deferring history save (e.g. crop) so "save from ref"
    // in endElementEditing reads crop dimensions even if user unselects before React flushes setState.
    if (skipHistorySave && latestUpdatedImagesRef) {
      if (!latestUpdatedImagesRef.current) {
        latestUpdatedImagesRef.current = { main: null, offCanvas: null };
      }
      const hasCropOrDimensions =
        newProps.cropData != null ||
        (newProps.width != null && newProps.height != null);
      const sourceOffCanvas =
        latestUpdatedImagesRef.current.offCanvas ?? offCanvasImages;
      const existingInOffCanvas =
        Array.isArray(sourceOffCanvas) &&
        sourceOffCanvas.some((img) => img.id === id);
      if (
        Array.isArray(updatedOffCanvasImages) &&
        updatedOffCanvasImages.length > 0
      ) {
        latestUpdatedImagesRef.current.offCanvas = updatedOffCanvasImages;
      } else if (hasCropOrDimensions && existingInOffCanvas) {
        // Fallback: closure may not have had this id in offCanvasImages; apply crop to ref/current list
        const { isReplacement, isCropOperation, ...cleanNewProps } = newProps;
        const nextOff = sourceOffCanvas.map((img) =>
          img.id === id
            ? {
                ...img,
                ...cleanNewProps,
                width:
                  newProps.width !== undefined
                    ? Number(newProps.width)
                    : img.width,
                height:
                  newProps.height !== undefined
                    ? Number(newProps.height)
                    : img.height,
              }
            : img,
        );
        latestUpdatedImagesRef.current.offCanvas = nextOff;
      }
    }
    if (setOffCanvasImages) {
      // CRITICAL: Use functional update to avoid stale closure issues
      // This ensures we always work with the latest state, preventing position from reverting
      setOffCanvasImages((prevOffCanvasImages) => {
        // Recalculate the update based on the latest state to avoid stale data issues
        const latestImage = prevOffCanvasImages.find((img) => img.id === id);
        if (!latestImage) {
          // Image not found in latest state, return unchanged
          return prevOffCanvasImages;
        }

        // Apply newProps to the latest image state
        let processedProps = { ...newProps };

        // Handle aspect ratio maintenance if both width and height are being updated
        if (
          latestImage.maintainAspectRatio &&
          processedProps.width !== undefined &&
          processedProps.height !== undefined &&
          !processedProps.skipAspectRatioCorrection &&
          !processedProps.isReplacement &&
          !processedProps.isCropOperation
        ) {
          const aspectRatio =
            latestImage.originalAspectRatio ||
            latestImage.width / latestImage.height;
          const newWidth = Number(processedProps.width);
          const newHeight = Number(processedProps.height);
          const providedRatio = newWidth / newHeight;
          const ratioTolerance = 0.01;

          if (Math.abs(providedRatio - aspectRatio) > ratioTolerance) {
            const correctedHeight =
              Math.round((newWidth / aspectRatio) * 100) / 100;
            processedProps.height = correctedHeight;
          }
        }

        // Remove temporary flags
        const {
          isReplacement,
          isCropOperation,
          skipAspectRatioCorrection,
          ...cleanProps
        } =
          processedProps;

        // CRITICAL FIX: During drag operations, don't update position in updateImageProperties
        // Position will be finalized in handleImageDragEnd, and updating it here can cause race conditions
        // that lead to position reverting. Also don't update position immediately after drag end (cooldown period).
        // EXCEPTION: Manual input changes (isManualInput=true) should ALWAYS update position immediately
        const dragEndTime = dragEndTimestamps.get(id);
        const isInCooldown =
          dragEndTime && Date.now() - dragEndTime < DRAG_END_COOLDOWN_MS;
        // Use the same logic for all images: only update position when not dragging and (not in cooldown OR is manual input)
        const shouldUpdatePosition =
          !isDragging && (!isInCooldown || isManualInput);

        if (isInCooldown && !isManualInput) {
        }

        // Merge with latest state
        const merged = {
          ...latestImage,
          width:
            processedProps.width !== undefined
              ? Number(processedProps.width)
              : latestImage.width,
          height:
            processedProps.height !== undefined
              ? Number(processedProps.height)
              : latestImage.height,
          // Only update position if not dragging (position is finalized in handleImageDragEnd)
          x:
            shouldUpdatePosition && processedProps.x !== undefined
              ? Number(processedProps.x)
              : latestImage.x,
          y:
            shouldUpdatePosition && processedProps.y !== undefined
              ? Number(processedProps.y)
              : latestImage.y,
          rotation:
            processedProps.rotation !== undefined
              ? Number(processedProps.rotation)
              : latestImage.rotation,
          scaleX:
            processedProps.scaleX !== undefined
              ? Number(processedProps.scaleX)
              : latestImage.scaleX,
          scaleY:
            processedProps.scaleY !== undefined
              ? Number(processedProps.scaleY)
              : latestImage.scaleY,
          ...cleanProps,
        };

        // CRITICAL FIX: Update updatedImg to include position from newProps even when isDragging=true
        // This ensures ImageEditor's useEffect runs and updates the x/y input values during drag
        // IMPORTANT: Always update updatedImg with current position, even during drag
        // CRITICAL: For selectedObject/selectedImage, ALWAYS use the new position from processedProps if provided
        // Don't use merged.x/y as fallback because merged might have stale values due to cooldown
        // Instead, use latestImage.x/y as fallback to ensure we always have valid coordinates
        updatedImg = {
          ...merged,
          // CRITICAL: Use processedProps position if provided (for live updates), otherwise use latestImage position
          // This ensures selectedObject/selectedImage get live updates even when cooldown blocks array updates
          x:
            processedProps.x !== undefined
              ? Number(processedProps.x)
              : latestImage.x,
          y:
            processedProps.y !== undefined
              ? Number(processedProps.y)
              : latestImage.y,
          type: "Image",
        };

        const nextOffCanvas = prevOffCanvasImages.map((img) =>
          img.id === id ? merged : img,
        );
        // CRITICAL: When saving is deferred (e.g. crop), store in ref so endElementEditing can save even if selection clears first
        if (skipHistorySave && latestUpdatedImagesRef?.current) {
          latestUpdatedImagesRef.current.offCanvas = nextOffCanvas;
        }
        return nextOffCanvas;
      });
    }
  }

  if (updatedImg) {
    // CRITICAL: Match rectangle behavior - only update selectedObject when explicitly updating position
    // During drag, imageRenderer.js calls setSelectedObject directly (like rectangleRenderer does)
    // Calling it here with stale data from images array causes flickering
    const isUpdatingPosition =
      newProps.x !== undefined || newProps.y !== undefined;
    // CRITICAL: Also sync selection when isOnTopLayer (or other layer-affecting props) change
    // so that "Display on Top" checkbox reflects immediately without needing to select/unselect
    const isUpdatingLayer = newProps.isOnTopLayer !== undefined;
    // CRITICAL: Sync selection on crop so selectedObject has the new image; otherwise second crop
    // would show stale selection and the new cropped image wouldn't display until unselect.
    const isCropOperation = newProps.isCropOperation === true;
    if (
      setSelectedObject &&
      (isUpdatingPosition || isUpdatingLayer || isCropOperation)
    ) {
      setSelectedObject(updatedImg);
    }
    // CRITICAL: Only call setSelectedImage for manual input changes (or layer change for immediate UI sync)
    // During drag, imageRenderer.js handles setSelectedImage directly. For crop, always sync.
    // Also sync for any non-position update (e.g. referenceUrl) so editor shows updated value without reselect.
    const shouldSyncSelectedImage = isUpdatingPosition ? isManualInput : true;
    if (setSelectedImage && shouldSyncSelectedImage) {
      setSelectedImage(updatedImg);
    }
  }

  if (setHasUnsavedChanges) {
    setHasUnsavedChanges(true);
  }

  // CRITICAL: Don't save to history during continuous transforms (onTransform) or when called from onTransformEnd
  // History should only be saved in onTransformEnd handlers in the renderers
  // This prevents duplicate history entries during continuous transform operations
  // Only save to history if:
  // 1. This is NOT a continuous transform operation (isDragging=true means it's a continuous transform)
  // 2. skipHistorySave is false (when called from onTransformEnd, the renderer handles history)
  // The onTransformEnd handler in imageRenderer.js will save the final state
  if (skipHistorySave) {
    // When skipping history save (e.g., from ImageEditor height/width/rotation input), set pendingChanges
    // so element_edit_complete will save when element is unselected. Otherwise undo would remove the image
    // instead of reverting to previous height. Set whenever we actually updated an image (updatedImg).
    const shouldSetPending = !!updatedImg;
    if (setPendingChanges && shouldSetPending) {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }

      // CRITICAL: Store the updated images array in ref so element_edit_complete can use it
      if (latestUpdatedImagesRef) {
        const isImageInMainCanvas =
          Array.isArray(images) && images.some((img) => img.id === id);
        const isImageInOffCanvas =
          Array.isArray(offCanvasImages) &&
          offCanvasImages.some((img) => img.id === id);

        if (!latestUpdatedImagesRef.current) {
          latestUpdatedImagesRef.current = { main: null, offCanvas: null };
        }

        if (isImageInMainCanvas && Array.isArray(updatedImages)) {
          const existingRefMain = latestUpdatedImagesRef.current.main;
          // Detect bitmap-only updates: newProps has no crop / dimension overrides.
          // These come from background compression callbacks which use a stale images
          // closure and can carry outdated crop geometry (either no cropData, or the
          // cropData from a previous crop before a Reset). We protect against both by
          // comparing dimensions: a bitmap-only update must never change width/height.
          const isBitmapOnlyUpdate =
            newProps.cropData == null && newProps.width == null && newProps.height == null;
          const cleanedUpdatedImages = updatedImages.map((img) => {
            const { isTransform, ...cleanedImg } = img;
            if (isBitmapOnlyUpdate && Array.isArray(existingRefMain)) {
              const existingRefImg = existingRefMain.find((r) => r.id === cleanedImg.id);
              if (
                existingRefImg &&
                (existingRefImg.width !== cleanedImg.width ||
                  existingRefImg.height !== cleanedImg.height)
              ) {
                // The stale closure computed different dimensions — preserve all crop
                // and geometry data already stored in the ref.
                return {
                  ...cleanedImg,
                  cropData: existingRefImg.cropData,
                  width: existingRefImg.width,
                  height: existingRefImg.height,
                  originalWidth: existingRefImg.originalWidth,
                  originalHeight: existingRefImg.originalHeight,
                  originalAspectRatio: existingRefImg.originalAspectRatio,
                  imageDataUrl: existingRefImg.imageDataUrl,
                  originalImageDataUrl: existingRefImg.originalImageDataUrl,
                  originalCropRotation: existingRefImg.originalCropRotation,
                  flipHorizontal: existingRefImg.flipHorizontal,
                  flipVertical: existingRefImg.flipVertical,
                  hasBakedTransforms: existingRefImg.hasBakedTransforms,
                  // Stale closure often still has pre-crop rotation; ref was set with baked 0° + scale 1
                  rotation: existingRefImg.rotation ?? cleanedImg.rotation ?? 0,
                  scaleX: existingRefImg.scaleX ?? cleanedImg.scaleX ?? 1,
                  scaleY: existingRefImg.scaleY ?? cleanedImg.scaleY ?? 1,
                };
              }
            }
            return cleanedImg;
          });
          latestUpdatedImagesRef.current.main = cleanedUpdatedImages;
        } else if (
          isImageInOffCanvas &&
          Array.isArray(updatedOffCanvasImages)
        ) {
          const existingRefOffCanvas = latestUpdatedImagesRef.current.offCanvas;
          const isBitmapOnlyUpdate =
            newProps.cropData == null && newProps.width == null && newProps.height == null;
          const cleanedUpdatedImages = updatedOffCanvasImages.map((img) => {
            const { isTransform, ...cleanedImg } = img;
            if (isBitmapOnlyUpdate && Array.isArray(existingRefOffCanvas)) {
              const existingRefImg = existingRefOffCanvas.find((r) => r.id === cleanedImg.id);
              if (
                existingRefImg &&
                (existingRefImg.width !== cleanedImg.width ||
                  existingRefImg.height !== cleanedImg.height)
              ) {
                return {
                  ...cleanedImg,
                  cropData: existingRefImg.cropData,
                  width: existingRefImg.width,
                  height: existingRefImg.height,
                  originalWidth: existingRefImg.originalWidth,
                  originalHeight: existingRefImg.originalHeight,
                  originalAspectRatio: existingRefImg.originalAspectRatio,
                  imageDataUrl: existingRefImg.imageDataUrl,
                  originalImageDataUrl: existingRefImg.originalImageDataUrl,
                  originalCropRotation: existingRefImg.originalCropRotation,
                  flipHorizontal: existingRefImg.flipHorizontal,
                  flipVertical: existingRefImg.flipVertical,
                  hasBakedTransforms: existingRefImg.hasBakedTransforms,
                  rotation: existingRefImg.rotation ?? cleanedImg.rotation ?? 0,
                  scaleX: existingRefImg.scaleX ?? cleanedImg.scaleX ?? 1,
                  scaleY: existingRefImg.scaleY ?? cleanedImg.scaleY ?? 1,
                };
              }
            }
            return cleanedImg;
          });
          latestUpdatedImagesRef.current.offCanvas = cleanedUpdatedImages;
        }
      }
    }
  } else if (saveToHistory && !isDragging) {
    // Only save to history for non-continuous operations (e.g., property changes from editor)
    // Transform operations are handled by onTransformEnd in the renderer
    // CRITICAL: Don't save if element is being edited - let element_edit_complete handle it
    const isEditing = isElementBeingEditedRef?.current ?? false;
    if (!isEditing) {
      setTimeout(() => {
        // Check if we're currently undoing/redoing to prevent clearing redo stack
        if (isUndoingRedoing && isUndoingRedoing.current) {
          return;
        }
        saveToHistory("update_image_properties");
      }, 0);
    } else {
     
    }
  }

  return updatedImg;
};

/**
 * Updates video properties
 * @param {string} id - Video ID
 * @param {Object} newProps - New properties to apply
 * @param {Array} videos - Current videos array
 * @param {Array} offCanvasVideos - Current off-canvas videos array
 * @param {Function} setVideos - Function to set videos
 * @param {Function} setOffCanvasVideos - Function to set off-canvas videos
 * @param {Function} setSelectedObject - Function to set selected object
 * @param {Function} setSelectedVideo - Function to set selected video
 * @param {Function} setHasUnsavedChanges - Function to set unsaved changes flag
 * @param {Function} generateVideoThumbnail - Function to generate video thumbnail
 * @param {Object} selectedVideo - Currently selected video
 * @returns {Object} Updated video object
 */
export const updateVideoProperties = (
  id,
  newProps,
  videos,
  offCanvasVideos,
  setVideos,
  setOffCanvasVideos,
  setSelectedObject,
  setSelectedVideo,
  setHasUnsavedChanges,
  generateVideoThumbnail,
  selectedVideo,
) => {
  let updatedVid = null;

  const updatedVideos = videos.map((vid) => {
    if (vid.id === id) {
      const merged = {
        ...vid,
        ...newProps,
        width:
          newProps.width !== undefined ? Number(newProps.width) : vid.width,
        height:
          newProps.height !== undefined ? Number(newProps.height) : vid.height,
        x: newProps.x !== undefined ? Number(newProps.x) : vid.x,
        y: newProps.y !== undefined ? Number(newProps.y) : vid.y,
        rotation:
          newProps.rotation !== undefined
            ? Number(newProps.rotation)
            : vid.rotation,
      };
      updatedVid = { ...merged, type: "Video" };
      return merged;
    }
    return vid;
  });

  setVideos(updatedVideos);

  // Also update off-canvas videos
  const updatedOffCanvasVideos = offCanvasVideos.map((vid) => {
    if (vid.id === id) {
      const merged = {
        ...vid,
        ...newProps,
        width:
          newProps.width !== undefined ? Number(newProps.width) : vid.width,
        height:
          newProps.height !== undefined ? Number(newProps.height) : vid.height,
        x: newProps.x !== undefined ? Number(newProps.x) : vid.x,
        y: newProps.y !== undefined ? Number(newProps.y) : vid.y,
        rotation:
          newProps.rotation !== undefined
            ? Number(newProps.rotation)
            : vid.rotation,
      };
      updatedVid = { ...merged, type: "Video" };
      return merged;
    }
    return vid;
  });
  setOffCanvasVideos(updatedOffCanvasVideos);

  if (updatedVid) {
    setSelectedObject(updatedVid);
    setSelectedVideo(updatedVid);
  }

  setHasUnsavedChanges(true);

  // If source changed, regenerate duration and thumbnail asynchronously
  if (newProps && Object.prototype.hasOwnProperty.call(newProps, "src")) {
    const newSrc = newProps.src;
    if (newSrc) {
      const tempVideo = document.createElement("video");
      tempVideo.preload = "metadata";
      tempVideo.src = newSrc;
      const applyMeta = async () => {
        let duration = 0;
        try {
          duration = isFinite(tempVideo.duration) ? tempVideo.duration : 0;
        } catch (e) {}
        const thumbnail = await generateVideoThumbnail(newSrc, 320, 180);
        setVideos((prev) =>
          prev.map((v) => (v.id === id ? { ...v, duration, thumbnail } : v)),
        );
        setOffCanvasVideos((prev) =>
          prev.map((v) => (v.id === id ? { ...v, duration, thumbnail } : v)),
        );
        if (selectedVideo?.id === id) {
          const nextSel = {
            ...selectedVideo,
            duration,
            thumbnail,
            src: newSrc,
            type: "Video",
          };
          setSelectedObject(nextSel);
          setSelectedVideo(nextSel);
        }
      };
      tempVideo.onloadedmetadata = () => {
        applyMeta();
      };
      tempVideo.onerror = () => {
        applyMeta();
      };
    }
  }

  return updatedVid;
};

/**
 * Handles image drag end operation
 * @param {string} id - Image ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {number} width - New width
 * @param {number} height - New height
 * @param {Array} images - Current images array
 * @param {Function} setImages - Function to set images
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 */
export const handleImageDragEnd = (
  id,
  x,
  y,
  width,
  height,
  images,
  setImages,
  saveToHistory,
  isUndoingRedoing,
  currentImage = null, // Optional: pass current image object to preserve all properties (rotation, scaleX, scaleY, etc.)
) => {
  // CRITICAL: Record drag end timestamp BEFORE state update to prevent updateImageProperties
  // from updating position with stale data after drag ends. This ensures that any calls to
  // updateImageProperties after drag ends will be blocked by the cooldown.
  dragEndTimestamps.set(id, Date.now());

  // Clean up old timestamps (older than cooldown period)
  const now = Date.now();
  for (const [imageId, timestamp] of dragEndTimestamps.entries()) {
    if (now - timestamp > DRAG_END_COOLDOWN_MS) {
      dragEndTimestamps.delete(imageId);
    }
  }

  // Safety check: ensure images is an array
  if (!Array.isArray(images)) {
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date images array
  if (!setImages) {
    console.error("🟢 [HANDLE-IMAGE-DRAG-END] setImages is not available!", {
      id,
      x,
      y,
      setImagesType: typeof setImages,
    });
    return;
  }

  setImages((prevImages) => {
    // CRITICAL: Find the current image to preserve all properties
    // If currentImage is provided, use it; otherwise find it from the current state
    // This ensures we preserve rotation, scaleX, scaleY, and all other properties
    const existingImage =
      currentImage || prevImages.find((img) => img.id === id);

    if (!existingImage) {
      return prevImages; // Return unchanged if image not found
    }

    const isTopLayerImage = existingImage?.isOnTopLayer === true;

    // CRITICAL: Preserve ALL properties from the existing image (rotation, scaleX, scaleY, etc.)
    // CRITICAL FIX: ALWAYS use the x, y coordinates passed to this function (from drag end),
    // NOT the coordinates from existingImage. The existingImage might have stale coordinates
    // from before the drag, especially for top-layer images where position is updated during drag.
    // This is the key fix: we always use the new position from the drag end event, not the old position
    // from the existing image state.
    const updatedImages = prevImages.map((img) =>
      img.id === id
        ? {
            ...existingImage, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
            x, // CRITICAL: Always use the new position from drag end, not from existingImage
            y, // CRITICAL: Always use the new position from drag end, not from existingImage
            width,
            height,
          }
        : img,
    );

    // Debug logging to verify position and rotation are preserved
    const updatedImage = updatedImages.find((img) => img.id === id);
    if (updatedImage) {
      const positionChanged =
        Math.abs(updatedImage.x - (existingImage?.x || 0)) > 0.1 ||
        Math.abs(updatedImage.y - (existingImage?.y || 0)) > 0.1;

      if (
        !positionChanged &&
        (Math.abs(x - (existingImage?.x || 0)) > 0.1 ||
          Math.abs(y - (existingImage?.y || 0)) > 0.1)
      ) {
        console.error(
          "🟢 [HANDLE-IMAGE-DRAG-END] CRITICAL: Position was NOT updated even though new position differs!",
          {
            id,
            requestedX: x,
            requestedY: y,
            existingX: existingImage?.x,
            existingY: existingImage?.y,
            updatedX: updatedImage.x,
            updatedY: updatedImage.y,
          },
        );
      }
    } else {
      console.error(
        "🟢 [HANDLE-IMAGE-DRAG-END] Updated image not found in updatedImages!",
        {
          id,
          updatedImagesLength: updatedImages.length,
          updatedImagesIds: updatedImages.map((img) => img.id),
        },
      );
    }

    // Save to history with the updated images
    // CRITICAL: Call saveToHistory OUTSIDE the setImages callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedImages;
      }

      // CRITICAL: Save drag operations immediately, not when element is unselected
      // This ensures drag operations create separate history entries that can be undone independently
      // Pass the updated images array as override to ensure saveToHistory gets the correct state
      setTimeout(() => {
        saveToHistory("image_drag_end", updatedImages);
      }, 0);
    }

    return updatedImages;
  });
};

// Track last drag end call to prevent duplicates
let lastDragEndCall = null;
const DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

/**
 * Create a function to handle image drag end with bound dependencies
 * @param {Array} images - Current images array
 * @param {Function} setImages - Function to set images
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle image drag end
 */
export const createHandleImageDragEnd = (
  images,
  setImages,
  saveToHistory,
  isUndoingRedoing,
) => {
  return (id, x, y, width, height, currentImage = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleImageDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}-${width}-${height}`;

    if (
      lastDragEndCall &&
      lastDragEndCall.key === callKey &&
      now - lastDragEndCall.timestamp < DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }

    // Update last call tracking
    lastDragEndCall = {
      key: callKey,
      timestamp: now,
    };

    return handleImageDragEnd(
      id,
      x,
      y,
      width,
      height,
      images,
      setImages,
      saveToHistory,
      isUndoingRedoing,
      currentImage, // Pass the current image object to preserve all properties (rotation, scaleX, scaleY, etc.)
    );
  };
};

/**
 * Create a function to handle image click
 * @param {Array} images - Current images array
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedObject - Setter for selected object
 * @returns {Function} Function to handle image click
 */
export const createHandleImageClick =
  (images, setSelectedImage, setSelectedObject) => (imageId) => {
    setSelectedImage((prev) =>
      prev?.id === imageId ? null : images.find((img) => img.id === imageId),
    );
    setSelectedObject((prev) =>
      prev?.id === imageId
        ? null
        : { ...images.find((img) => img.id === imageId), type: "Image" },
    );
  };
